/* ---------------------------------------------------------------------
 *                           Import Packages
 * --------------------------------------------------------------------- */
// React
import React, { useState, useEffect } from "react";

// React Router
import {
  useActionData,
  useSubmit,
  useNavigate,
} from "react-router-dom";

// Components
import LoginDialog from "./LoginDialog";
import { SiJiaSnack } from "../common/Common";

// utils
import {
  validateMessageLogin,
  validatePasswordLogin,
  validateRegister,
  validateResetPassword,
} from "../../lib/validate/authValidity";
import {
  loginUser,
  registerUser,
  resetPassword,
  sendSmsCode,
} from "../../lib/service/userLogin";

// CSS
import "../../assets/css/m-mzw.css";
import "../../assets/css/hdr_ftr.css";
import "../../assets/css/mzw-style.css";

// Images
import logo from "../../assets/images/logo1.png";
import picA1 from "../../assets/images/pic-a1.png";
import qrcode from "../../assets/images/qrcode1.png";
import iconA2 from "../../assets/images/icon-a2-1.png";
import bgPicA2 from "../../assets/images/bgpic-a2.png";
import iconA3 from "../../assets/images/icon-a3-1.png";
import bannerBg from "../../assets/images/banner-a1.jpg";

/* -------------------------------------------
 *              Loader & Action
 * -------------------------------------------*/

/**
 * 登录/注册的loader函数
 * @returns {boolean}
 */
export async function loader() {
  return true;
}

/**
 * 登录/注册的action函数
 * @param {request} request 请求对象
 * @returns {object}
 */
export async function action({ request }) {
  try {
    const formData = await request.formData();
    const type = formData.get("type");
    const userObj = JSON.parse(formData.get("userObj"));

    let result;
    switch (type) {
      case "messageLogin":
        result = await loginUser("message", userObj);
        return { ...result, type: "login" };
      case "passwordLogin":
        result = await loginUser("password", userObj);
        return { ...result, type: "login" };
      case "register":
        result = await registerUser(userObj);
        return result;
      case "resetPassword":
        result = await resetPassword(userObj);
        return result;
      default:
        return { success: false, message: "服务器错误" };
    }
  } catch (error) {
    return { success: false, message: error?.message || "服务器错误" };
  }
}

/* ---------------------------------------------------------------------
 *                           Login Component
 * --------------------------------------------------------------------- */

/**
 * 定义登录组件
 * @returns 登录组件
 * @function Login
 * @static
 */
export default function Login() {
  /* -------------------------------------------
   *                  Snackbar
   * -------------------------------------------*/
  /**
   * 显示Snackbar
   * @type {Object}
   */
  const [snackbarMes, setSnackbarMes] = useState({
    open: false,
    text: "",
    severity: "warning",
  });

  /**
   * 处理Snackbar关闭事件
   * @param {string} reason - 关闭原因
   * @returns {Function}
   */
  const handleSnackbarClose = (reason) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbarMes((prev) => ({ ...prev, open: false }));
  };

  /* -------------------------------------------
   *                LoginDialog
   * -------------------------------------------*/

  /**
   * 登录对话框状态
   * @type {boolean}
   */
  const [openLoginDialog, setOpenLoginDialog] = useState(false);

  /**
   * 打开登录对话框
   * @returns {void}
   */
  const handleOpenLoginDialog = () => {
    setOpenLoginDialog(true);
  };

  /**
   * 关闭登录对话框
   * @returns {void}
   */
  const handleCloseLoginDialog = () => {
    setOpenLoginDialog(false);
  };

  /* -------------------------------------------
   *                Auth Submit
   * -------------------------------------------*/

  /**
   * 提交表单
   * @type {Function}
   */
  const submit = useSubmit();

  /**
   * 表单提交结果
   * @type {Object}
   */
  const res = useActionData();

  /**
   * 导航
   * @type {object}
   */
  const navigate = useNavigate();

  /**
   * 处理登录/注册操作
   * @param {string} type 操作类型
   * @param {object} userObj 用户对象
   * @returns {Promise}
   */
  const handleAuth = async (type, userObj) => {
    try {
      // 如果是发送验证码，直接调用 API
      if (type === "sendCode") {
        const result = await sendSmsCode({ mobile: userObj.mobile });
        return result;
      }

      // 验证函数映射
      const validationMap = {
        messageLogin: validateMessageLogin,
        passwordLogin: validatePasswordLogin,
        register: validateRegister,
        resetPassword: validateResetPassword,
      };

      // 获取对应的验证函数
      const validateFn = validationMap[type];
      if (!validateFn) {
        throw new Error(`未知的认证类型: ${type}`);
      }

      // 验证数据
      const validUserObj = validateFn(userObj);

      // 提交数据
      submit(
        {
          type,
          userObj: JSON.stringify(validUserObj),
        },
        {
          method: "post",
          encType: "application/x-www-form-urlencoded",
        }
      );
    } catch (error) {
      const errText = Array.isArray(error.errors)
        ? error.errors.join("，")
        : error.message || "验证失败";

      setSnackbarMes({
        open: true,
        text: errText,
        severity: "error",
      });
    }
  };

  /**
   * 处理表单提交结果
   * @type {Function}
   */
  useEffect(() => {
    if (res) {
      if (res.success === false) {
        setSnackbarMes({
          text: res.message,
          open: true,
          severity: "error",
        });
      } else if (res.success === true && res.type === "login") {
        setSnackbarMes({
          text: res.message,
          open: true,
          severity: "success",
        });
        setTimeout(() => {
          navigate("/");
        }, 2000);
      } else {
        setSnackbarMes({
          text: res.message,
          open: true,
          severity: "success",
        });
      }
    }
  }, [navigate, res]);

  /* -------------------------------------------
   *                Load Scripts
   * -------------------------------------------*/

  /**
   * 动态导入平台图片
   * @returns
   */
  const platformImages = Array.from({ length: 11 }).map((_, idx) => {
    return require(`../../assets/images/pic-a2-${idx + 1}.jpg`);
  });

  /**
   * 加载脚本文件
   * @param {string} src - 脚本路径
   * @returns {Promise} - 返回Promise对象
   */
  const loadScript = (src) => {
    return new Promise((resolve, reject) => {
      const script = document.createElement("script");
      script.src = src;
      script.async = true;
      script.onload = resolve;
      script.onerror = reject;
      document.body.appendChild(script);
    });
  };

  /**
   * 首页组件加载时执行
   * @return {void}
   */
  useEffect(() => {
    const loadScripts = async () => {
      try {
        await loadScript("/js/jquery-1.11.3.min.js");

        // 在所有脚本加载完成后执行内联脚本
        const inlineScript = document.createElement("script");
        inlineScript.innerHTML = `
          $(function () {
            setTimeout(function(){
                $('body').removeClass('mask');
            },500)
            $('.g-mzw-fixed').mouseenter(function () {
                $(this).find('.box').stop().fadeIn();
            }).mouseleave(function () {
                $(this).find('.box').stop().fadeOut();
            });
          });
        `;
        document.body.appendChild(inlineScript);
      } catch (error) {
        console.error("Error loading scripts:", error);
      }
    };

    // 加载脚本
    loadScripts();

    // 添加滚动监听
    const handleScroll = () => {
      const sections = document.querySelectorAll(".section");
      const navItems = document.querySelectorAll(".header .nav li");

      sections.forEach((section, index) => {
        const rect = section.getBoundingClientRect();
        // 当section的顶部进入视口时，更新导航状态
        if (rect.top <= 100 && rect.bottom >= 100) {
          navItems.forEach((item, i) => {
            if (i === index) {
              item.classList.add("on");
            } else {
              item.classList.remove("on");
            }
          });
        }
      });
    };

    // 添加滚动事件监听
    window.addEventListener("scroll", handleScroll);

    // 清理函数
    return () => {
      const scripts = document.querySelectorAll("script");
      scripts.forEach((script) => {
        if (script.src.includes("jquery") || !script.src) {
          document.body.removeChild(script);
        }
      });
      // 移除滚动事件监听
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  /* ---------------------------------------------------------------------
   *                            Render Component
   * --------------------------------------------------------------------- */

  return (
    <>
      {/* 顶栏 */}
      <div className="header">
        <div className="wp1280">
          <div className="hdr-con">
            <a href="/" className="logo">
              <div className="icon mzw-img1">
                <img src={logo} alt="四加AI Logo" />
              </div>
              <div className="txt">四加——AI跨境运营先驱</div>
            </a>
            <ul className="nav">
              <li className="on">
                <span className="a1">首页</span>
              </li>
              <li>
                <span className="a1">关于我们</span>
              </li>
              <li>
                <button className="a1" onClick={handleOpenLoginDialog}>
                  登录
                </button>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* 首页 */}
      <div className="main-content">
        <div className="section" id="section1">
          <div
            className="mzw-banner mzw-bg1"
            style={{ backgroundImage: `url(${bannerBg})` }}
          >
            <div className="wp1280">
              <div className="con">
                <div className="txt">
                  <div className="tit">欢迎来到这里——AI运营四加</div>
                  <div className="desc">
                    <p>四加是一个跨境电商AI运营助理，</p>
                    <p>它可以帮助跨境小白轻松地把产品卖到全球</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="row-a1">
            <div className="wp1280">
              <div className="row-a1-1">
                <div className="g-mzw-tit1">
                  主要功能<span>/MAIN FUNCTION</span>
                </div>
              </div>
              <div className="row-a1-2">
                <ul className="mzw-list1 mzw-clear">
                  {["全球多平台", "引导式运营", "最佳运营策略"].map(
                    (title, idx) => (
                      <li key={idx}>
                        <div className="con">
                          <div className="box">
                            <div className="icon mzw-img1">
                              <img src={iconA2} alt={title} />
                            </div>
                          </div>
                          <div className="txt">
                            <div className="tit">{title}</div>
                            <div className="line"></div>
                            <div className="desc">
                              {idx === 0 &&
                                "您可以轻松管理全球多个电商平台或者社媒的运营。"}
                              {idx === 1 &&
                                "AI会引导您或直接完成每一步操作，即使您是什么也不会。"}
                              {idx === 2 &&
                                "AI会根据您的情况，给出适合您的运营方案。"}
                            </div>
                          </div>
                        </div>
                      </li>
                    )
                  )}
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* 关于我们 */}
        <div className="section" id="section2">
          <div className="row-a2">
            <div className="wp1280">
              <div className="mzw-box1">
                <div className="pic mzw-img1">
                  <img src={picA1} alt="关于我们" />
                </div>
                <div className="con">
                  <div className="tit">
                    <div className="g-mzw-tit1">
                      关于我们<span>/ ABOUT US</span>
                    </div>
                  </div>
                  <div className="line"></div>
                  <div className="txt">
                    此刻风行成都贸易有限公司是一家长期专注于跨境电商综合运营服务的企业，我们拥有长达8年的跨境运营经验。包含全球主流平台亚马逊、速卖通、Ozon、Tiktok、Shopee、Shein、Temu、Lazada、Walmart、Shopify、Coupang等。我们致力于为卖家降低运营成本，提高运营效率。在全球跨境市场激烈的竞争中，人工智能的出现是未来卖家成功的核心因素之一，我们希望让卖家不再为运营烦恼~
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div
            className="row-a3 index1 mzw-bg1"
            style={{ backgroundImage: `url(${bgPicA2})` }}
          >
            <div className="wp1280">
              <div className="row-a3-1">
                <div className="g-mzw-tit1">
                  合作平台<span>/ COOPERATION PLATFORM</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 底栏 */}
        <div className="section" id="section3">
          <div
            className="row-a3 index2 mzw-bg1"
            style={{ backgroundImage: `url(${bgPicA2})` }}
          >
            <div className="wp1280">
              <div className="rowa-3-2">
                <ul className="mzw-list2 mzw-clear">
                  {platformImages.map((imgSrc, idx) => (
                    <li key={idx}>
                      <a href="#" className="a1 mzw-img1">
                        <img src={imgSrc} alt={`Platform ${idx + 1}`} />
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
          <div className="footer mzw-bg1">
            <div className="wp1280">
              <div className="ftr-con">
                <div className="ftr-info">
                  <div className="tit">
                    <div className="g-mzw-tit1">
                      联系我们<span>/CONTACT US</span>
                    </div>
                  </div>

                  <div className="txt">
                    <p>邮箱：2323921591@qq.com</p>
                    <p>合作：18782072272（微信同号）</p>
                  </div>
                </div>
                <div className="ftr-qrcode mzw-img1">
                  <img src={qrcode} alt="联系我们二维码" />
                </div>
              </div>
              <div className="ftr-beian-hao">
                <p>
                  <a href="http://beian.miit.gov.cn/">
                    网站备案号：2030602186750
                  </a>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="g-mzw-fixed">
        <div className="icon mzw-img1">
          <img src={iconA3} alt="微信咨询" />
        </div>
        <div className="txt">微信咨询</div>
        <div className="box mzw-img1">
          <img src={qrcode} alt="微信二维码" />
        </div>
      </div>

      {/* 登录弹窗 */}
      <LoginDialog
        isOpenDialog={openLoginDialog}
        onSubmit={handleAuth}
        onCloseDialog={handleCloseLoginDialog}
      />

      {/* Snackbar */}
      <SiJiaSnack
        open={snackbarMes.open}
        text={snackbarMes.text}
        severity={snackbarMes.severity}
        onClose={handleSnackbarClose}
      />
    </>
  );
}
